import { VideoHandlerBase } from '../Abstract';
import Hls, { ErrorData, Events } from 'hls.js';
import { BaseError } from 'make-error';
import { Media, SimpleHandlerConfig } from '../types';
import { InvalidHlsStream, isHlsStreamValid } from '../../Utility/Hls';
import { VideoLoadError } from '../errors';
import { Log } from '../../../AuxillaryService/Log';

class HlsNotSupported extends BaseError {
    constructor(message: string) {
        super(message);
    }
}

interface HlsPlayer {
    hls: Hls;
    player: HTMLVideoElement;
}

export class HlsMediaSourceStreamHandler extends VideoHandlerBase<HlsPlayer> {
    protected _type: string;
    protected _videoId: string;
    protected _videoUrl: string;

    /**
     * A UNIX timestamp tracking the last time a media error occurred
     */
    #lastMediaErrorTime: number;

    constructor(config: SimpleHandlerConfig) {
        super(config);
        this._videoId = config.videoUrl;
        this._videoUrl = config.videoUrl;
        this._type = 'hls';

        this.#lastMediaErrorTime = 0;
    }

    protected async _createNewPlayer(): Promise<HlsPlayer> {
        if (!Hls.isSupported()) {
            const message =
                'Unable to initialize hls stream because this device does not support HLS';
            throw new HlsNotSupported(message);
        }

        const isValidStream = await isHlsStreamValid(this._videoUrl);
        if (!isValidStream) {
            const message =
                'Unable to initialize hls stream because it is not properly formatted or unavailable';
            throw new InvalidHlsStream(message);
        }

        const element = this.getElement();
        const player = document.createElement('video');
        element.appendChild(player);

        const hlsInstance = new Hls({ enableWorker: false });
        hlsInstance.attachMedia(player);

        //todo: This error handling is very simplistic, almost straight from the docs.
        //  Improve approach, add protections for error loops.
        hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) {
                const now = Date.now();
                let message: string;
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        message =
                            'Fatal Network Error playing this HLS Stream, attempting to recover';
                        hlsInstance.startLoad();
                        break;
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        if (now - this.#lastMediaErrorTime < 1000) {
                            //if we have multiple MEDIA_ERRORs
                            //within a short time period,
                            //there is likely an audio issue
                            message = 'Fatal Media error during HLS playback. Swapping audio Codec';
                            hlsInstance.swapAudioCodec();
                        } else {
                            message =
                                'Fatal Media error during HLS playback. Attempting to recover';
                        }

                        this.#lastMediaErrorTime = now;
                        hlsInstance.recoverMediaError();
                        break;
                    default:
                        message = 'HLS stream error was not recoverable';
                        hlsInstance.destroy();
                        break;
                }

                Log.Error(message, null, __fileline, data);
            } else {
                Log.Info(
                    `Non-fatal HLS Video Stream Error: '${data.type}'`,
                    null,
                    __fileline,
                    data
                );
            }
        });

        player.addEventListener('ended', async () => {
            this.eventEmitter.emitEvent(Media.Ended);
        });

        try {
            this.#loadVideo(hlsInstance);
        } catch (err) {
            if (err instanceof VideoLoadError) {
                Log.Debug(
                    `Remove player element after it failed to load. ${err.message}`,
                    null,
                    __fileline,
                    { error: err }
                );
                hlsInstance.destroy();
                player.parentNode?.removeChild(player);
            }
            throw err;
        }

        return {
            hls: hlsInstance,
            player,
        };
    }

    protected async _setCurrentTime(seconds: number): Promise<void> {
        const { hls } = this._getAttachedPlayer();
        hls.startLoad(seconds);
    }

    protected async _doPlay(): Promise<void> {
        const { player } = this._getAttachedPlayer();
        await player.play();
    }

    protected async _doPause(): Promise<void> {
        const { player } = this._getAttachedPlayer();
        await player.pause();
    }

    protected async _doDestroyPlayer(): Promise<void> {
        const { hls } = this._getPossiblyDetachedPlayer();
        hls.destroy();
    }

    async #loadVideo(hlsInstance: Hls): Promise<void> {
        return new Promise((resolve, reject) => {
            //When the manifest is successfully parsed, the video stream will be available for play
            hlsInstance.once(Hls.Events.MANIFEST_PARSED, () => {
                Log.Debug('HLS manifest parsed successfully', null, __fileline);
                resolve();
            });

            const errorListener = (_event: Events.ERROR, data: ErrorData) => {
                if (
                    data.fatal &&
                    data.type !== Hls.ErrorTypes.MEDIA_ERROR &&
                    data.type !== Hls.ErrorTypes.NETWORK_ERROR
                ) {
                    hlsInstance.off(Hls.Events.ERROR, errorListener);
                    reject(new VideoLoadError(`Fatal error: ${data.details}`));
                    return;
                }
            };
            hlsInstance.on(Hls.Events.ERROR, errorListener);

            hlsInstance.loadSource(this._videoUrl);
        });
    }
}
